/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode } from 'react';

import cn from 'classnames';

import type { BridgeSize } from 'ui/molecules/SectionBridge/SectionBridge.types';
import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './HeroSection.module.scss';

interface Stat {
  value: string;
  label: string;
}

interface Props {
  title?: ReactNode;
  description?: ReactNode;
  extra?: ReactNode;
  icon?: ReactNode;
  iconOffset?: string;
  actions?: ReactNode;
  media?: ReactNode;
  mediaClassName?: string;
  mediaPosition?: 'right' | 'bottom';
  stats?: Stat[];
  align?: 'center' | 'left';
  background?: string;
  maxWidth?: string;
  bridgeBottom?: BridgeSize;
  className?: string;
  descriptionClassName?: string;
  footer?: ReactNode;
  // Legacy backward-compat props
  buttonText?: string;
  buttonHref?: string;
  type?: string;
  disabled?: boolean;
  transparent?: boolean;
}

export const HeroSection = ({
  title,
  description,
  extra,
  icon,
  iconOffset,
  actions,
  media,
  mediaClassName,
  mediaPosition = 'bottom',
  stats,
  align = 'center',
  background,
  maxWidth,
  bridgeBottom,
  className,
  descriptionClassName,
  footer,
  buttonText,
  buttonHref,
  type: _type,
  disabled: _disabled,
  transparent: _transparent,
}: Props) => (
  <section
    className={cn(
      styles.root,
      align === 'left' && styles.alignLeft,
      !!(media && mediaPosition === 'right') && styles.split,
      bridgeBottom && styles[`bridgeBottom_${bridgeBottom}`],
      className,
    )}
    style={background ? { background } : undefined}
  >
    <div className={styles.inner} style={maxWidth ? { maxWidth, marginInline: 'auto', width: '100%' } : undefined}>
      <div className={styles.content}>
        {icon && (
          <div
            className={styles.icon}
            style={iconOffset ? { transform: `translateY(${iconOffset})`, position: 'relative', zIndex: 1 } : undefined}
          >
            {icon}
          </div>
        )}

        {(title || description) && (
          <TitleBlock
            size="page"
            align={align}
            title={title}
            description={description}
            descriptionClassName={descriptionClassName}
            className={styles.titleBlock}
          />
        )}

        {extra && <div className={styles.extra}>{extra}</div>}

        {(actions || buttonText) && (
          <div className={styles.actions}>
            {actions}
            {!actions && buttonText && (
              <Button type="dark" size="xxl" route={buttonHref}>
                {buttonText}
              </Button>
            )}
          </div>
        )}

        {stats && (
          <div className={styles.stats}>
            {stats.map((stat, i) => (
              <div key={i} className={styles.stat}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {media && (
        <div className={cn(styles.media, mediaPosition === 'bottom' && styles.mediaBottom, mediaClassName)}>
          {media}
        </div>
      )}

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  </section>
);
